import { API_BASE_URL, STIP_BASE_URL } from "../props";
import { equals, mergeLeft, not, omit, pickBy } from "ramda";
import { notBlank } from "../util/ext";
import {
  useGet,
  useGetWithNext,
  useGetWithCount,
  usePut,
  usePost,
  useDelete,
} from "../common/fetchServices";
import { getQueryParams } from "../util/fetchUtils";
import { useCurrentOrgId, useIsAdmin } from "../auth/authUtils";

// 5 * 60 * 1000 === 5 Minutes
const RefetchInterval = {
  PENDING: 5 * 60 * 1000,
  PROCESSING: 5 * 60 * 1000,
};

const getAnthologyCollectionsUrl = id =>
  `${API_BASE_URL}/creator/anthologies/${id}/collections`;
const getAnthologyCollectionsCountUrl = id =>
  `${API_BASE_URL}/creator/anthologies/${id}/collections/count`;
const getAdminMediaUrl = id => `${API_BASE_URL}/admin/media/${id}`;
const getContentUrl = id => `${API_BASE_URL}/creator/content/${id}`;
const getContentStatusUrl = status =>
  `${API_BASE_URL}/creator/content/status/${status}`;
const getModuleContentGroupsUrl = id =>
  `${API_BASE_URL}/creator/modules/${id}/content-groups`;
const getProcessingStatusUrl = orgId => `${STIP_BASE_URL}/creator/processing`;
const getAcknowledgeFailureUrl = ({ orgId, submissionId }) =>
  `${STIP_BASE_URL}/creator/processing/${orgId}/${submissionId}/acknowledge-failure`;
const getRejectFailureUrl = ({ orgId, submissionId }) =>
  `${STIP_BASE_URL}/creator/processing/${orgId}/${submissionId}/reject-failure`;
const getProjectLinkUrl = ({ id }) =>
  `${API_BASE_URL}/admin/media/${id}/project-link`;

const organizedContentGroupsUrl = `${API_BASE_URL}/creator/content-groups/organized`;
const unorganizedContentGroupsUrl = `${API_BASE_URL}/creator/content-groups/unorganized`;

const buildUrl = (url, params) => {
  const queryParams = getQueryParams(params);
  return url + "?" + queryParams;
};

export const useContent = id => {
  const isAdmin = useIsAdmin();
  const url = isAdmin ? getAdminMediaUrl(id) : getContentUrl(id);
  return useGet(["creator-content", { id }], url);
};

export const usePendingItems = (limit = 5) => {
  const url = `${API_BASE_URL}/admin/media/status/pending?limit=${limit}`;
  return useGet(["pending-items"], url);
};

export const useUnorganizedItems = (limit = 5) => {
  const url = `${API_BASE_URL}/admin/media/status/unorganized?limit=${limit}`;
  return useGet(["unorganized-items"], url);
};

export const useCreatorModules = params => {
  const orgId = useCurrentOrgId();
  const url = `${API_BASE_URL}/creator/modules?${getQueryParams(params)}`;
  return useGetWithNext(
    ["modules-creator", orgId, url],
    url,
    {},
    {
      enabled: true,
      refetchOnMount: true,
    },
  );
};

export const useModuleContentGroups = (id, params, queryOptions) => {
  const orgId = useCurrentOrgId();
  const url = buildUrl(getModuleContentGroupsUrl(id), params);
  return useGetWithNext(
    ["module-content-groups", orgId, url],
    url,
    {},
    mergeLeft(queryOptions, { enabled: true, refetchOnMount: true }),
  );
};

const buildAdminMediaUrl = params => {
  const queryParams = getQueryParams(params);
  const url = `${API_BASE_URL}/admin/media?${queryParams}`;
  return url;
};

export const useAdminMedia = ({
  limit = 10,
  offset = 0,
  sort = "auditUpdated desc",
  mediaType = "module,anthology,collection",
  isParent = null,
  hasParent = null,
  refetchOnMount = false,
}) => {
  const orgId = useCurrentOrgId();

  const url = buildAdminMediaUrl({
    limit,
    offset,
    sort,
    mediaType,
    isParent,
    hasParent,
  });

  return useGetWithNext(
    ["admin-media", orgId, { url }],
    url,
    {},
    { refetchOnMount },
  );
};

export const useAdminMediaAll = ({
  sort = "auditUpdated desc",
  mediaType = "module,anthology,collection",
  limit = "all",
}) => {
  const orgId = useCurrentOrgId();
  const url = buildAdminMediaUrl({
    limit,
    mediaType,
    orgId,
    sort,
  });
  return useGet(["admin-media-all", orgId, { url }], url);
};

const buildContentByStatusUrl = params => {
  const queryParams = getQueryParams(omit(["status"], params));
  const url = `${getContentStatusUrl(params.status)}?${queryParams}`;

  return url;
};

export const pendingQueryOptions = {
  refetchInterval: RefetchInterval.PENDING,
  refetchOnMount: "always",
  refetchOnWindowFocus: "always",
  refetchIntervalInBackground: true,
  staleTime: 0,
};

export const useContentByStatus = (
  {
    status = "approved",
    limit = 10,
    offset = 0,
    sort = "media.auditAdded desc",
    type = "",
    hasParent = null,
    source = null,
    q = "",
  },
  queryOptions = {},
) => {
  const orgId = useCurrentOrgId();

  const url = buildContentByStatusUrl({
    status,
    limit,
    offset,
    sort,
    type,
    hasParent,
    source,
    q,
  });

  return useGetWithCount(
    ["content-status", orgId, { url }],
    url,
    {},
    pickBy(notBlank, queryOptions),
  );
};

export const useAnthologyCollectionsCreator = (
  id,
  params = {},
  queryOptions = {},
) => {
  const orgId = useCurrentOrgId();
  const queryParams = getQueryParams(params);
  const url = getAnthologyCollectionsUrl(id) + "?" + queryParams;
  return useGetWithNext(
    ["anthology-collections-creator", orgId, { url, id }],
    url,
    {},
    queryOptions,
  );
};

export const useAnthologyCollectionsCount = (id, params = {}) => {
  const queryParams = "?" + getQueryParams(params);
  const url = getAnthologyCollectionsCountUrl(id) + queryParams;
  const orgId = useCurrentOrgId();
  return useGet(["anthology-collections-count", orgId, { url, id }], url);
};

const buildUnorganizedContentGroupsUrl = params => {
  const queryParams = "?" + getQueryParams(params);
  const url = unorganizedContentGroupsUrl + queryParams;
  return url;
};

export const useUnorganizedContentGroups = ({
  limit = 10,
  offset = 0,
  sort = "auditUpdated desc",
  mediaType = "anthology,collection",
  q = "",
  refetchOnMount = false,
}) => {
  const orgId = useCurrentOrgId();

  const url = buildUnorganizedContentGroupsUrl({
    limit,
    offset,
    sort,
    mediaType,
    q,
  });

  return useGetWithNext(
    ["unorganized-content-groups", orgId, { url }],
    url,
    {},
    { refetchOnMount },
  );
};

const buildOrganizedContentGroupsUrl = params => {
  const queryParams = "?" + getQueryParams(params);
  const url = organizedContentGroupsUrl + queryParams;
  return url;
};

export const useOrganizedContentGroups = ({
  limit = 10,
  offset = 0,
  sort = "auditUpdated desc",
  mediaType = "anthology,collection",
  q = "",
  refetchOnMount = false,
}) => {
  const orgId = useCurrentOrgId();

  const url = buildOrganizedContentGroupsUrl({
    limit,
    offset,
    sort,
    mediaType,
    q,
  });

  return useGetWithNext(
    ["organized-content-groups", orgId, { url }],
    url,
    {},
    { refetchOnMount },
  );
};

export const useContentInProcessing = (orgId, params) => {
  const url = getProcessingStatusUrl(orgId);
  const props = {
    page: params.page,
    size: params.limit,
    sort: params.sort,
    query: notBlank(params.query) ? params.query : null,
    source: notBlank(params.source) ? params.source : null,
    contentTypes: notBlank(params.contentTypes) ? params.contentTypes : null,
    statuses: notBlank(params.statuses) ? params.statuses : null,
    organizationId: not(equals(orgId, "admin")) ? orgId : null,
  };

  const queryUrl = notBlank(params) ? url + "?" + getQueryParams(props) : url;

  const refetchOnMount = "always";
  return useGet(
    ["content-processing", { queryUrl }],
    queryUrl,
    true,
    refetchOnMount,
    {
      refetchInterval: RefetchInterval.PROCESSING,
      refetchIntervalInBackground: true,
    },
  );
};

export const useAcknowledgeFailure = () => {
  return usePut(getAcknowledgeFailureUrl, {
    queriesToInvalidate: [["content-processing"]],
  });
};

export const useRejectFailure = () => {
  return usePut(getRejectFailureUrl, {
    queriesToInvalidate: [["content-processing"]],
  });
};

export const useProjectLink = id => {
  const url = getProjectLinkUrl({ id });
  return useGet(["project-link", { id }], url);
};

export const useAddProjectLink = () => {
  return usePost(getProjectLinkUrl, {
    queriesToInvalidate: [["project-link", ["id"]]],
  });
};

export const useDeleteProjectLink = () => {
  return useDelete(getProjectLinkUrl, {
    queriesToInvalidate: [["project-link", ["id"]]],
  });
};
